import { useState, useRef, useEffect, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import { VideoInteractionService } from '../services/videoInteractionService';
import { getEpisodesForSeries, ContentItem, getContentById } from '../services/contentService';
import { getUserProfile } from '../services/userService';
import { ViewTrackingService } from '../services/viewTrackingService';

interface UseVideoPlayerProps {
  videoId: string;
  contentId: string;
  userUid?: string;
  title?: string;
  creatorId?: string;
}

export const useVideoPlayer = ({ videoId, contentId, userUid, title, creatorId }: UseVideoPlayerProps) => {
  const toast = useToast();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging] = useState(false);
  const [showSkipFeedback, setShowSkipFeedback] = useState(false);
  const [skipAmount, setSkipAmount] = useState(0);
  const [hoverTime, setHoverTime] = useState<number | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [bufferedRanges, setBufferedRanges] = useState<TimeRanges | null>(null);
  const [showControls, setShowControls] = useState(true);
  const [likes, setLikes] = useState(0);
  const [dislikes, setDislikes] = useState(0);
  const [views, setViews] = useState(0);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [isDisliked, setIsDisliked] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [isLocked, setIsLocked] = useState(false);
  const [showEpisodes, setShowEpisodes] = useState(false);
  const [showSubtitles, setShowSubtitles] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [episodes, setEpisodes] = useState<ContentItem[]>([]);
  const [isLoadingEpisodes, setIsLoadingEpisodes] = useState(false);
  const [fetchedCreatorAvatar, setFetchedCreatorAvatar] = useState('');
      const [viewCount, setViewCount] = useState(0);
      const [hasWatched30Seconds, setHasWatched30Seconds] = useState(false);
      const [isUniqueViewRecorded, setIsUniqueViewRecorded] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout>();

  const formatTime = (seconds: number) => {
    if (!seconds || !isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isValidVideoSource = (src: string): boolean => {
    if (!src || typeof src !== 'string') return false;
    
    try {
      const url = new URL(src);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const getProgressPercentage = () => {
    if (!duration) return 0;
    return Math.round((currentTime / duration) * 100);
  };

  const handlePlayPause = useCallback(async () => {
    if (isLocked) {
      toast({
        title: 'Player is locked',
        description: 'Unlock the player to control playback.',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    if (!videoRef.current || !videoId) {
      toast({
        title: 'No video source available',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      if (isPlaying) {
        videoRef.current.pause();
        if (userUid && contentId && currentTime > 0) {
          VideoInteractionService.recordWatchTime(contentId, userUid, Math.floor(currentTime));
        }
      } else {
        await videoRef.current.play();
        // Start tracking watch time for YouTube-style view recording
        if (userUid && contentId && !isUniqueViewRecorded) {
          startWatchTimeTracking();
        }
      }
      setIsPlaying(!isPlaying);
    } catch (error) {
      console.error('Video play error:', error);
      toast({
        title: 'Video playback error',
        description: 'Unable to play video. Please check the video source.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [isPlaying, isLocked, userUid, contentId, currentTime, toast]);

  const handleProgressMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!duration) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const time = Math.max(0, Math.min(duration, percent * duration));
    setHoverTime(time);
    setShowPreview(true);
  };

  const handleProgressMouseLeave = () => {
    setShowPreview(false);
    setHoverTime(null);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isLocked) {
      toast({
        title: 'Player is locked',
        description: 'Unlock the player to seek.',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    if (!duration) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const time = Math.max(0, Math.min(duration, percent * duration));
    
    setCurrentTime(time);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
    }
  };

  const getBufferedPercent = () => {
    if (!bufferedRanges || !duration || bufferedRanges.length === 0) return 0;
    
    let bufferedEnd = 0;
    for (let i = 0; i < bufferedRanges.length; i++) {
      if (bufferedRanges.start(i) <= currentTime && bufferedRanges.end(i) > currentTime) {
        bufferedEnd = bufferedRanges.end(i);
        break;
      }
    }
    
    return (bufferedEnd / duration) * 100;
  };

  const handleSkip = useCallback((seconds: number) => {
    if (isLocked) {
      toast({
        title: 'Player is locked',
        description: 'Unlock the player to skip.',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    if (!videoRef.current || !duration) return;

    const newTime = Math.max(0, Math.min(duration, currentTime + seconds));
    setCurrentTime(newTime);
    videoRef.current.currentTime = newTime;

    setSkipAmount(seconds);
    setShowSkipFeedback(true);
    setTimeout(() => setShowSkipFeedback(false), 1000);
  }, [isLocked, currentTime, duration, toast]);

  const handleLike = useCallback(async () => {
    if (!userUid || !contentId) return;

    try {
      const newLikedState = !isLiked;
      
      // Optimistic update
      setIsLiked(newLikedState);
      if (newLikedState && isDisliked) {
        setIsDisliked(false);
      }
      
      // Perform the actual toggle
      const success = await VideoInteractionService.toggleLike(contentId, userUid, newLikedState);
      
      if (success) {
        // Fetch fresh counts from database to ensure accuracy
        const stats = await VideoInteractionService.getVideoStats(contentId);
        if (stats) {
          setLikes(Math.max(0, stats.likes || 0));
          setDislikes(Math.max(0, stats.dislikes || 0));
        }
      } else {
        // Revert optimistic update on failure
        setIsLiked(!newLikedState);
        if (newLikedState && isDisliked) {
          setIsDisliked(true);
        }
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      // Revert optimistic update on error
      setIsLiked(!isLiked);
      if (isDisliked) {
        setIsDisliked(true);
      }
    }
  }, [userUid, contentId, isLiked, isDisliked]);

  const handleDislike = useCallback(async () => {
    if (!userUid || !contentId) return;

    try {
      const newDislikedState = !isDisliked;
      
      // Optimistic update
      setIsDisliked(newDislikedState);
      if (newDislikedState && isLiked) {
        setIsLiked(false);
      }
      
      // Perform the actual toggle
      const success = await VideoInteractionService.toggleDislike(contentId, userUid, newDislikedState);
      
      if (success) {
        // Fetch fresh counts from database to ensure accuracy
        const stats = await VideoInteractionService.getVideoStats(contentId);
        if (stats) {
          setLikes(Math.max(0, stats.likes || 0));
          setDislikes(Math.max(0, stats.dislikes || 0));
        }
      } else {
        // Revert optimistic update on failure
        setIsDisliked(!newDislikedState);
        if (newDislikedState && isLiked) {
          setIsLiked(true);
        }
      }
    } catch (error) {
      console.error('Error toggling dislike:', error);
      // Revert optimistic update on error
      setIsDisliked(!isDisliked);
      if (isLiked) {
        setIsLiked(true);
      }
    }
  }, [userUid, contentId, isLiked, isDisliked]);

  const fetchCreatorStats = useCallback(async () => {
    if (!creatorId) return;
    
    try {
      const [creatorStats, profile] = await Promise.all([
        VideoInteractionService.getCreatorStats(creatorId),
        getUserProfile(creatorId)
      ]);
      
      if (creatorStats) {
        setSubscriberCount(creatorStats.subscribers || 0);
      }
      
      if (profile && profile.avatarUrl) {
        setFetchedCreatorAvatar(profile.avatarUrl);
      }
    } catch (error) {
      console.error('Error fetching creator stats:', error);
    }
  }, [creatorId]);

  const fetchSubscriptionStatus = useCallback(async () => {
    if (!userUid || !creatorId) return;
    
    try {
      const subscriptions = await VideoInteractionService.getUserSubscriptions(userUid);
      setIsSubscribed(subscriptions.includes(creatorId));
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  }, [userUid, creatorId]);

  const handleSubscribe = useCallback(async () => {
    if (!userUid || !creatorId) return;

    try {
      const newSubscribedState = !isSubscribed;
      await VideoInteractionService.toggleSubscription(creatorId, userUid, newSubscribedState);
      setIsSubscribed(newSubscribedState);
      
      // Refresh creator stats and subscription status
      await Promise.all([
        fetchCreatorStats(),
        fetchSubscriptionStatus()
      ]);
    } catch (error) {
      console.error('Error toggling subscription:', error);
    }
  }, [userUid, creatorId, isSubscribed, fetchCreatorStats, fetchSubscriptionStatus]);

  const handleShare = useCallback(async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Check out this video',
          text: `Check out "${title}" on Vindie Stream!`,
          url: window.location.href,
        });
        // Share was successful (user didn't cancel)
        toast({
          title: 'Video shared successfully',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast({
          title: 'Link copied to clipboard',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error) {
      // Handle share cancellation or other errors
      if (error instanceof Error && error.name === 'AbortError') {
        // User canceled the share dialog - this is normal, don't show error
        return;
      }
      
      // For other errors, fallback to clipboard
      console.error('Share error:', error);
      try {
        await navigator.clipboard.writeText(window.location.href);
        toast({
          title: 'Link copied to clipboard',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } catch (clipboardError) {
        console.error('Clipboard error:', clipboardError);
        toast({
          title: 'Unable to share or copy link',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  }, [toast, title]);

  const handleDonate = useCallback(() => {
    // This will be handled by the parent component
  }, []);

  const handleLockToggle = useCallback(() => {
    setIsLocked(!isLocked);
    toast({
      title: isLocked ? 'Player unlocked' : 'Player locked',
      description: isLocked ? 'You can now control playback' : 'Use Shift+L to unlock',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  }, [isLocked, toast]);

  const fetchEpisodes = useCallback(async () => {
    setIsLoadingEpisodes(true);
    try {
      // Check if contentId is an episode ID (format: seriesId-s1-e1)
      if (contentId.includes('-s') && contentId.includes('-e')) {
        const seriesId = contentId.split('-s')[0];
        
        // Get the series content to fetch episodes
        const seriesContent = await getContentById(seriesId);
        
        if (seriesContent && (seriesContent as any).seasons && Array.isArray((seriesContent as any).seasons)) {
          const seasonsData = (seriesContent as any).seasons;
          const allEpisodes: ContentItem[] = [];
          
          seasonsData.forEach((season: any, seasonIndex: number) => {
            const seasonEpisodes: ContentItem[] = season.episodes.map((episode: any, episodeIndex: number) => ({
              id: `${seriesId}-s${seasonIndex + 1}-e${episodeIndex + 1}`,
              title: episode.title,
              description: episode.description,
              category: 'episode',
              thumbnail: seriesContent.thumbnail || seriesContent.poster || '',
              assets: {
                video: episode.videoUrl,
                videoFile: episode.videoUrl
              },
              seasonNumber: (seasonIndex + 1).toString(),
              episodeNumber: (episodeIndex + 1).toString(),
              uploader: seriesContent.uploader,
              uploaderId: seriesContent.uploaderId,
              duration: 45, // Default duration
              views: 0,
              likes: 0,
              dislikes: 0,
              comments: 0,
              createdAt: seriesContent.createdAt,
              updatedAt: seriesContent.updatedAt
            }));
            
            allEpisodes.push(...seasonEpisodes);
          });
          
          setEpisodes(allEpisodes);
          return;
        }
      }
      
      // First try to get the content to check if it has seasons data
      const currentContent = await getContentById(contentId);
      
      if (currentContent && (currentContent as any).seasons && Array.isArray((currentContent as any).seasons)) {
        // New data structure: seasons stored in series document
        const seasonsData = (currentContent as any).seasons;
        const allEpisodes: ContentItem[] = [];
        
        seasonsData.forEach((season: any, seasonIndex: number) => {
          const seasonEpisodes: ContentItem[] = season.episodes.map((episode: any, episodeIndex: number) => ({
            id: `${contentId}-s${seasonIndex + 1}-e${episodeIndex + 1}`,
            title: episode.title,
            description: episode.description,
            category: 'episode',
            thumbnail: currentContent.thumbnail || currentContent.poster || '',
            assets: {
              video: episode.videoUrl,
              videoFile: episode.videoUrl
            },
            seasonNumber: (seasonIndex + 1).toString(),
            episodeNumber: (episodeIndex + 1).toString(),
            uploader: currentContent.uploader,
            uploaderId: currentContent.uploaderId,
            duration: 45, // Default duration
            views: 0,
            likes: 0,
            dislikes: 0,
            comments: 0,
            createdAt: currentContent.createdAt,
            updatedAt: currentContent.updatedAt
          }));
          
          allEpisodes.push(...seasonEpisodes);
        });
        
        setEpisodes(allEpisodes);
        return;
      }
      
      // Fallback to old method for backward compatibility
      const seriesEpisodes = await getEpisodesForSeries(contentId);
      
      if (seriesEpisodes.length === 0) {
        try {
          if (currentContent && (currentContent as any).linkedContent) {
            const seriesId = (currentContent as any).linkedContent;
            const linkedEpisodes = await getEpisodesForSeries(seriesId);
            setEpisodes(linkedEpisodes);
            return;
          }
        } catch (seriesError) {
          // Silently fail - will use empty episodes array
        }
      }
      
      setEpisodes(seriesEpisodes);
    } catch (error) {
      console.error('Error fetching episodes:', error);
      toast({
        title: 'Error loading episodes',
        description: 'Failed to load episodes for this series',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingEpisodes(false);
    }
  }, [contentId, toast]);

  const handleEpisodesToggle = useCallback(() => {
    const newShowEpisodes = !showEpisodes;
    setShowEpisodes(newShowEpisodes);
    
    if (newShowEpisodes && episodes.length === 0) {
      fetchEpisodes();
    }
  }, [showEpisodes, episodes.length, fetchEpisodes]);

  const handleSubtitlesToggle = useCallback(() => {
    const newShowSubtitles = !showSubtitles;
    setShowSubtitles(newShowSubtitles);
    
    if (videoRef.current && videoRef.current.textTracks) {
      for (let i = 0; i < videoRef.current.textTracks.length; i++) {
        videoRef.current.textTracks[i].mode = newShowSubtitles ? 'showing' : 'disabled';
      }
    }
  }, [showSubtitles]);

  const handleMuteToggle = useCallback(() => {
    if (!videoRef.current) return;
    
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    videoRef.current.muted = newMutedState;
    
    toast({
      title: newMutedState ? 'Muted' : 'Unmuted',
      status: 'info',
      duration: 1500,
      isClosable: true,
    });
  }, [isMuted, toast]);

  const handleFullscreenToggle = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  const handleDownload = useCallback(() => {
    if (!videoId) {
      toast({
        title: 'No video available',
        description: 'Cannot download video',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const link = document.createElement('a');
    link.href = videoId;
    link.download = `${title || 'video'}.mp4`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: 'Download started',
      description: 'Video download has been initiated',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  }, [videoId, toast]);

  const handleRefresh = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.load();
      toast({
        title: 'Video refreshed',
        status: 'info',
        duration: 1500,
        isClosable: true,
      });
    }
  }, [toast]);

  const showControlsTemporarily = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  }, []);

  // Function to get the final avatar URL (either from props or fetched)
  const getCreatorAvatar = useCallback((propAvatar?: string) => {
    return propAvatar || fetchedCreatorAvatar;
  }, [fetchedCreatorAvatar]);

  // YouTube-style view tracking: record view after 30 seconds of watching
  const recordUniqueView = useCallback(async () => {
    if (!userUid || !contentId || isUniqueViewRecorded) return;
    
    try {
      // Check if user has already viewed this video
      const hasViewed = await VideoInteractionService.hasUserViewedVideo(contentId, userUid);
      if (hasViewed) {
        setIsUniqueViewRecorded(true);
        return;
      }

      const contentType = 'episode';
      const watchDuration = currentTime || 0;
      const contentDuration = duration || 0;
      
      const success = await ViewTrackingService.recordView(
        contentId,
        contentType,
        userUid,
        watchDuration,
        contentDuration
      );

      // Also record in videoInteractionService for consistency
      await VideoInteractionService.recordView(contentId, userUid, watchDuration);
      
      if (success) {
        setViewCount(prev => prev + 1);
        setIsUniqueViewRecorded(true);
      }
    } catch (error) {
      console.error('Error recording unique view:', error);
    }
  }, [userUid, contentId, isUniqueViewRecorded, currentTime, duration]);

  // Start tracking watch time for YouTube-style view recording
  const startWatchTimeTracking = useCallback(() => {
    if (!videoRef.current) return;

    const checkWatchTime = () => {
      if (videoRef.current && videoRef.current.currentTime >= 30 && !hasWatched30Seconds) {
        setHasWatched30Seconds(true);
        recordUniqueView();
      }
    };

    // Check every second
    const interval = setInterval(checkWatchTime, 1000);
    
    // Clean up interval when component unmounts or video pauses
    return () => clearInterval(interval);
  }, [hasWatched30Seconds, recordUniqueView]);

  // Load episodes when component mounts
  useEffect(() => {
    if (contentId && episodes.length === 0) {
      fetchEpisodes();
    }
  }, [contentId, episodes.length, fetchEpisodes]);

  // Load creator stats when component mounts
  useEffect(() => {
    if (creatorId) {
      fetchCreatorStats();
      fetchSubscriptionStatus();
    }
  }, [creatorId, fetchCreatorStats, fetchSubscriptionStatus]);

  // Load video data when component mounts
  useEffect(() => {
    const loadVideoData = async () => {
      if (!contentId || !userUid) return;
      
      setIsLoading(true);
      try {
        const [stats, interaction] = await Promise.all([
          VideoInteractionService.getVideoStats(contentId),
          VideoInteractionService.getUserInteraction(contentId, userUid)
        ]);
        
        if (stats) {
          setLikes(stats.likes || 0);
          setDislikes(stats.dislikes || 0);
          setViews(stats.views || 0);
        }

        // Load view count from new tracking service
        const viewStats = await ViewTrackingService.getContentViewStats(contentId);
        if (viewStats) {
          setViewCount(viewStats.totalViews || 0);
        }
        
        if (interaction) {
          setIsLiked(interaction.liked || false);
          setIsDisliked(interaction.disliked || false);
        }
        
        // Record view
        await VideoInteractionService.recordView(contentId, userUid);
      } catch (error) {
        console.error('Error loading video data:', error);
        toast({
          title: 'Error loading video data',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    loadVideoData();
  }, [contentId, userUid, toast]);

  return {
        // State
        isPlaying,
        currentTime,
        duration,
        isDragging,
        showSkipFeedback,
        skipAmount,
        hoverTime,
        showPreview,
        bufferedRanges,
        showControls,
        likes,
        dislikes,
        views,
        viewCount,
        subscriberCount,
        isLiked,
        isDisliked,
        isSubscribed,
        isLoading,
        playbackSpeed,
        isLocked,
        showEpisodes,
        showSubtitles,
        isMuted,
        isFullscreen,
        episodes,
        isLoadingEpisodes,
        fetchedCreatorAvatar,
        hasWatched30Seconds,
        isUniqueViewRecorded,
    
    // Refs
    videoRef,
    controlsTimeoutRef,
    
    // Functions
    formatTime,
    isValidVideoSource,
    getProgressPercentage,
    handlePlayPause,
    handleProgressMouseMove,
    handleProgressMouseLeave,
    handleProgressClick,
    getBufferedPercent,
    handleSkip,
    handleLike,
    handleDislike,
    handleSubscribe,
    handleShare,
    handleDonate,
    handleLockToggle,
    handleEpisodesToggle,
    handleSubtitlesToggle,
    handleMuteToggle,
    handleFullscreenToggle,
    handleDownload,
    handleRefresh,
    showControlsTemporarily,
    fetchEpisodes,
    fetchCreatorStats,
    fetchSubscriptionStatus,
    getCreatorAvatar,
    
    // Setters
    setCurrentTime,
    setDuration,
    setBufferedRanges,
    setShowControls,
    setLikes,
    setDislikes,
    setViews,
    setIsLiked,
    setIsDisliked,
    setIsSubscribed,
    setIsLoading,
    setPlaybackSpeed,
    setIsLocked,
    setShowEpisodes,
    setShowSubtitles,
    setIsMuted,
    setIsFullscreen,
    setEpisodes,
    setIsLoadingEpisodes,
    setIsPlaying,
    setViewCount,
    setHasWatched30Seconds,
    setIsUniqueViewRecorded
  };
};
